import { DateTime } from 'luxon'

import { formatDate, matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { findStatusByKey } from '@functions/status'

import type { OrderItemModel, OrderItemTaskModel } from '@models/types'

import type { EntitiesState } from '@redux/entities'

export type FilterCompletionStatusKey = 'complete' | 'current'

export type OrderItemTaskGroup = {
  groupKey: string,
  orderItemTaskIds: number[],
}

export type GroupedOrderItemTasks = {
  [key: string]: OrderItemTaskGroup,
}

export const splitTaskAddress = (fullAddress = '') => {
  const regex = /.+,/
  const matchAddress = fullAddress.match(regex)

  const address = matchAddress ? matchAddress.toString().replace(/,/, '') : fullAddress
  const suburb = matchAddress ? fullAddress.replace(regex, '').trimStart() : ''

  return {
    address,
    suburb,
  }
}

type SortOrderItemTaskGroupsParams = {
  groupedOrderItemTasks: GroupedOrderItemTasks,
  filterCompletionStatusKey: FilterCompletionStatusKey,
}

export const sortOrderItemTaskGroups = (params: SortOrderItemTaskGroupsParams) => {
  const { groupedOrderItemTasks, filterCompletionStatusKey } = params

  const array = Object.values(groupedOrderItemTasks)
  const arrayDeepCopy: OrderItemTaskGroup[] = JSON.parse(JSON.stringify(array))

  const sortedArray = arrayDeepCopy.sort((a, b) => {
    const sortA = DateTime.fromFormat(a.groupKey, 'dd/MM/yyyy')
    const sortB = DateTime.fromFormat(b.groupKey, 'dd/MM/yyyy')

    if (sortA > sortB) return filterCompletionStatusKey === 'complete' ? -1 : 1
    if (sortA < sortB) return filterCompletionStatusKey === 'complete' ? 1 : -1
    return 0
  })

  const noDateGroupIndex = sortedArray.findIndex(group => group.groupKey === 'No Date')
  const noDateGroup = noDateGroupIndex !== -1 && sortedArray[noDateGroupIndex]

  if (!!noDateGroup && sortedArray[0] !== noDateGroup){
    sortedArray.splice(noDateGroupIndex, 1)
    sortedArray.unshift(noDateGroup)
  }

  return sortedArray
}

type GroupOrderItemTasksParams = {
  filterCompletionStatusKey: FilterCompletionStatusKey,
  orderItemTasks: OrderItemTaskModel[],
}

export const groupOrderItemTasks = (params: GroupOrderItemTasksParams) => {
  const { orderItemTasks, filterCompletionStatusKey } = params

  // sort orderItemTasks by their sort_order before being grouped
  const sortedOrderItemTasks = sortArrayBy(Object.values(orderItemTasks), 'asc', 'sort_order')

  const grouped: GroupedOrderItemTasks = {}

  sortedOrderItemTasks.forEach((orderItemTask) => {
    const orderItemTaskDate = filterCompletionStatusKey === 'current'
      ? orderItemTask?.due_date
      : orderItemTask?.completed_date.date

    const groupDueDate = orderItemTaskDate || 'No Date'

    if (!grouped[groupDueDate]){
      grouped[groupDueDate] = {
        groupKey: groupDueDate,
        orderItemTaskIds: [],
      }
    }

    grouped[groupDueDate].orderItemTaskIds.push(orderItemTask.id)
  })

  return sortOrderItemTaskGroups({ groupedOrderItemTasks: grouped, filterCompletionStatusKey })
}

type MatchCompletionStatusKeyParams = {
  filterCompletionStatusKey?: FilterCompletionStatusKey,
  orderItem: OrderItemModel,
  status_id: number,
  statuses: EntitiesState['statuses'],
}

export const matchCompletionStatusKey = (params: MatchCompletionStatusKeyParams) => {
  const {
    filterCompletionStatusKey, orderItem, statuses, status_id,
  } = params

  const completeStatus = findStatusByKey(statuses, 'complete')
  // @ts-expect-error TS2339
  const matchCompleteStatus = matchFilterNumber(status_id, completeStatus?.id)

  if (filterCompletionStatusKey === 'complete') return matchCompleteStatus

  // Exclude tasks where the order item is pending and tasks that are complete
  if (filterCompletionStatusKey === 'current'){
    if (matchCompleteStatus) return false
    if (!orderItem.id) return true

    const pendingStatus = findStatusByKey(statuses, 'pending')
    // @ts-expect-error TS2339
    const isOrderItemPending = matchFilterNumber(orderItem.status_id, pendingStatus?.id)

    return !isOrderItemPending
  }

  return true
}

export const mapOrderItemTaskToModel = (orderItemTask: OrderItemTaskModel) => ({
  ...orderItemTask,
  due_date: formatDate(orderItemTask.due_date, 'dd/MM/yyyy'),
})
